import { useTranslation } from "react-i18next";
import React, { useCallback, useEffect, useState } from 'react';
import { Badge, ButtonGroup, Col, Dropdown, Form, FormLabel, Row } from 'react-bootstrap';
import FilterAccordion from "../../../Common/Filter/FilterAccordion";
import InputDate from "../../../Forms/Element/InputDate";
import { errorAlert, warnAlert } from "../../../alert/defaultAlert";
import { SearchOperation } from "../../../../../utils/ApiUtils";
import axiosInstance from "../../../../../services/AxiosInstance";
import Api from "../../../../../constants/Api";
import GridCustomBase from "../../../GridCustom/GridCustomBase";
import { formatDateTime, formatDateToYYYYMMDD, getFirstDayOfCurrentMonthDate } from "../../../../../utils/DateUtils";
import Common from "../../../../../constants/Common";
import InputText from "../../../Forms/Element/InputText";
import { useNavigate } from "react-router-dom";
import MenuConfig from "../../../../../constants/MenuConfig";
import { useBranch } from "../../../../../context/BranchContext";
import { convertDoubleToStringPTBR } from "../../../../../utils/ConverterUtils";
import CashStatusSelect from "./TransactionCashFlowTypeSelect";
import TransactionCashFlowType from "./TransactionCashFlowType";
import BranchSelect from "../../Main/Branch/BranchSelect";
import SaveIcon from "../../../Icon/SaveIcon";
import DetailIcon from "../../../Icon/DetailIcon";
import ModalPage from "../../../Common/Modal/ModalPage";
import "./TransactionCashFlowHistory.css";

/** Critério do card ativo na grade: Entradas ou Saídas. */
const cardCriteria = (card) => card
    ? [{ field: "type", value: card, operation: SearchOperation.EQUAL }]
    : [];

const TransactionCashFlowHistory = () => {

    const { t } = useTranslation();
    const { selectedBranch } = useBranch();
    // Começa no primeiro dia do mês corrente para já mostrar as movimentações do mês.
    const [startDate, setStartDate] = useState(getFirstDayOfCurrentMonthDate());
    const [endDate, setEndDate] = useState(new Date());
    const [user, setUser] = useState('');
    const [description, setDescription] = useState('');
    const [hideSubscriptions, setHideSubscriptions] = useState(false);
    const [type, setType] = useState(null);
    // Recorte escolhido num card (IN, OUT ou uma origem). É um só por vez e vale apenas para a
    // grade, como em Contas a pagar/receber: os cards continuam somando o período e os filtros
    // do formulário, senão clicar em "Entradas" zerava o card de saídas e mudava o saldo.
    const [cardFilter, setCardFilter] = useState(null);
    const [branchFilter, setBranchFilter] = useState(null);
    const [showLoading, setShowLoading] = useState(false);
    const [stats, setStats] = useState({
        totalIncome: 0, totalExpense: 0, balance: 0, count: 0,
    });
    const navigate = useNavigate();

    const [pageable, setPageable] = useState(null);
    const [page, setPage] = useState(0);
    const [rows, setRows] = useState([]);
    const [size, setSize] = useState(Common.DEFAULT_ROWS_PER_PAGE);
    const [showDetail, setShowDetail] = useState(false);
    const [selectedDetailRow, setSelectedDetailRow] = useState(null);

    const buildFilters = useCallback(() => {
        let filters = [];
        if (!startDate) {
            warnAlert(t('validation.required.start_date'));
            return null;
        }
        if (!endDate) {
            warnAlert(t('validation.required.end_date'));
            return null;
        }

        // Filtra pela data da movimentação (quando o dinheiro entrou/saiu), não pelo createdAt
        // (quando a linha foi gravada) - senão um pagamento confirmado com atraso pelo gateway,
        // ou uma baixa retroativa, apareceria no período errado.
        filters.push({
            field: "transactionDate",
            value: formatDateToYYYYMMDD(startDate) + "T00:00:00",
            operation: SearchOperation.GREATER_THAN_EQUAL
        });
        filters.push({
            field: "transactionDate",
            value: formatDateToYYYYMMDD(endDate) + "T23:59:59",
            operation: SearchOperation.LESS_THAN_EQUAL
        });

        const branchToFilter = selectedBranch !== null ? selectedBranch : branchFilter;
        if (branchToFilter !== null) {
            filters.push({
                field: "branch.id",
                value: branchToFilter.value || branchToFilter.id,
                operation: SearchOperation.EQUAL
            });
        }

        if (user) {
            filters.push({
                field: "cash.openingUser.firstName",
                value: user,
                operation: SearchOperation.MATCH
            });
        }

        if (description) {
            filters.push({
                field: "description",
                value: description,
                operation: SearchOperation.MATCH
            });
        }

        if (hideSubscriptions) {
            // Campo estruturado (origin) em vez de casar texto de descrição - populado
            // pelo backend em toda movimentação nova, e via backfill para o histórico. A
            // devolução de mensalidade estornada também é movimentação de assinatura. São duas
            // condições porque o NOT_IN da busca genérica só aceita números.
            ['SUBSCRIPTION', 'SUBSCRIPTION_REFUND'].forEach(origin => filters.push({
                field: "origin",
                value: origin,
                operation: SearchOperation.NOT_EQUAL
            }));
        }

        if (type?.value) {
            filters.push({
                field: "type",
                value: type.value,
                operation: SearchOperation.EQUAL
            });
        }

        return filters;
    }, [branchFilter, description, endDate, hideSubscriptions, selectedBranch, startDate, t, type, user]);


    const loadData = useCallback((filters, card, nextPage = page) => {
        let payload = {
            // A grade recebe o recorte do card; os cards, logo abaixo, não.
            filters: [...filters, ...cardCriteria(card)],
            page: nextPage,
            size,
            // Ordena pela data da movimentação: com lançamento retroativo e confirmação atrasada
            // de gateway, a ordem de inserção (id) não é mais a ordem cronológica do caixa.
            // O id entra como desempate para a paginação não embaralhar linhas do mesmo instante.
            orders: [{
                field: "transactionDate",
                direction: "DESC"
            }, {
                field: "id",
                direction: "DESC"
            }]
        };

        setShowLoading(true);

        const statsPayload = {
            filters,
            page: 0,
            size: 999999,
            orders: [{ field: "id", direction: "DESC" }]
        };

        axiosInstance.post(Api.cash_flow.transaction + "/pageable", statsPayload)
            .then((statsResponse) => {
                const allContent = statsResponse.data.content;
                const totalIncome = allContent
                    .filter(item => item.type === TransactionCashFlowType.IN)
                    .reduce((sum, item) => sum + item.value, 0);
                const totalExpense = allContent
                    .filter(item => item.type === TransactionCashFlowType.OUT)
                    .reduce((sum, item) => sum + item.value, 0);
                const balance = totalIncome - totalExpense;

                // Só os quatro números que qualquer pessoa confere sem explicação. Os cards por origem
                // (Assinatura, Venda, Manual) saíram: misturavam entrada e saída num valor só, e o
                // Manual chegava a ficar negativo, porque a baixa de contas a pagar cai nessa origem.
                // O recebido de assinaturas tem lugar próprio em Assinaturas > Cobranças; a origem
                // de cada movimentação continua no selo da grade.
                setStats({ totalIncome, totalExpense, balance, count: allContent.length });
            })
            .catch(error => console.error('Error loading stats:', error));

        axiosInstance.post(Api.cash_flow.transaction + "/pageable", payload)
            .then((response) => {
                setPageable(response.data);
                setRows(response.data.content);
            })
            .catch(error => errorAlert(error))
            .finally(() => {
                setShowLoading(false);
            });
    }, [page, size]);

    function search() {
        const filters = buildFilters();
        if (!filters) {
            return;
        }

        loadData(filters, cardFilter);
    }

    /**
     * Liga o recorte do card na grade; clicar de novo no card ativo desliga. O card Total passa
     * null e volta a mostrar tudo. Não mexe no campo Tipo do formulário: são filtros diferentes.
     */
    const handleCardClick = useCallback((card) => {
        setPage(0);
        setCardFilter(current => (card === null || current === card ? null : card));
    }, []);

    const redirectToNew = useCallback(() => {
        navigate(MenuConfig.cash_flow.transaction.route + "/new");
    }, [navigate]);

    const openDetail = (row) => {
        setSelectedDetailRow(row);
        setShowDetail(true);
    }

    useEffect(() => {
        if (selectedBranch !== null) {
            setBranchFilter({
                value: selectedBranch.id,
                label: selectedBranch.name
            });
        } else {
            setBranchFilter(null);
        }
    }, [selectedBranch]);

    useEffect(() => {
        search();
        // eslint-disable-next-line
    }, [page, size, selectedBranch, cardFilter]);

    const handleClearFilters = useCallback(() => {
        setStartDate(getFirstDayOfCurrentMonthDate());
        setEndDate(new Date());
        setUser('');
        setDescription('');
        setHideSubscriptions(false);
        setType(null);
        setCardFilter(null);
        if (selectedBranch !== null) {
            setBranchFilter({
                value: selectedBranch.id,
                label: selectedBranch.name
            });
        } else {
            setBranchFilter(null);
        }
        setPage(0);
    }, [selectedBranch]);

    return (
        <>
            <ModalPage
                title="Detalhes"
                show={showDetail}
                setShow={setShowDetail}
                size="lg"
                centered={true}
                child={
                    selectedDetailRow && (
                        <div className="transaction-history__detail">
                            <div className={`transaction-history__detail-hero ${selectedDetailRow.type === TransactionCashFlowType.OUT ? 'transaction-history__detail-hero--out' : 'transaction-history__detail-hero--in'}`}>
                                <div>
                                    <span className="transaction-history__detail-kicker">
                                        {selectedDetailRow.type === TransactionCashFlowType.IN ? t('cash_flow.income') : t('cash_flow.expense')}
                                    </span>
                                    <h5 className="mb-1 transaction-history__detail-title">{selectedDetailRow.description || '-'}</h5>
                                    <span className="text-muted">{selectedDetailRow.branch?.name || '-'}</span>
                                </div>
                                <strong className={selectedDetailRow.type === TransactionCashFlowType.OUT ? 'text-danger' : 'text-success'}>
                                    {t('currency_symbol')} {convertDoubleToStringPTBR(selectedDetailRow.value)}
                                </strong>
                            </div>

                            <div className="transaction-history__detail-grid">
                                <div className="transaction-history__detail-item">
                                    <span>{t('cash_flow.cash')}</span>
                                    <strong>{selectedDetailRow.cash?.openingUser?.fullName || '-'}</strong>
                                </div>
                                <div className="transaction-history__detail-item">
                                    <span>{t('branch')}</span>
                                    <strong>{selectedDetailRow.branch?.name || '-'}</strong>
                                </div>
                                <div className="transaction-history__detail-item">
                                    <span>{t('date_time')}</span>
                                    <strong>{formatDateTime(selectedDetailRow.transactionDate)}</strong>
                                </div>
                                <div className="transaction-history__detail-item">
                                    <span>{t('payment_method')}</span>
                                    <strong>{selectedDetailRow.paymentMethod ? t(selectedDetailRow.paymentMethod.toLowerCase()) : '-'}</strong>
                                </div>
                                <div className="transaction-history__detail-item transaction-history__detail-item--wide">
                                    <span>{t('description')}</span>
                                    <strong>{selectedDetailRow.description || '-'}</strong>
                                </div>
                            </div>
                        </div>
                    )
                }
            />

            <div className="transaction-history__filter-shell mb-4">
                <FilterAccordion
                    title={t('filters')}
                    onSearch={search}
                    onClear={handleClearFilters}
                    showQuickDates={true}
                    onQuickDateSelect={(start, end) => {
                        setStartDate(start);
                        setEndDate(end);
                    }}
                >
                    <Row className="g-3">
                        <Col lg={2} md={6}>
                            <FormLabel>{t('start_date')}</FormLabel>
                            <InputDate
                                onChange={(value) => {
                                    setStartDate(value);
                                }}
                                selected={startDate}
                            />
                        </Col>
                        <Col lg={2} md={6}>
                            <FormLabel>{t('end_date')}</FormLabel>
                            <InputDate
                                onChange={(value) => {
                                    setEndDate(value);
                                }}
                                selected={endDate}
                            />
                        </Col>
                        <Col lg={3} md={6}>
                            <FormLabel>{t('cash_flow.cash')}</FormLabel>
                            <InputText
                                value={user}
                                onChange={(e) => {
                                    setUser(e.target.value);
                                }}
                            />
                        </Col>
                        <Col lg={3} md={6}>
                            <FormLabel>{t('branch')}</FormLabel>
                            <BranchSelect
                                value={branchFilter}
                                onChange={(value) => {
                                    setBranchFilter(value);
                                }}
                                isDisabled={selectedBranch !== null}
                                isClearable={true}
                            />
                        </Col>
                        <Col lg={2} md={6}>
                            <FormLabel>{t('status')}</FormLabel>
                            <CashStatusSelect
                                value={type}
                                onChange={(value) => {
                                    setType(value);
                                }}
                                isClearable={true}
                            />
                        </Col>
                        <Col lg={4} md={6}>
                            <FormLabel>{t('description')}</FormLabel>
                            <InputText
                                value={description}
                                onChange={(e) => {
                                    setDescription(e.target.value);
                                }}
                            />
                        </Col>
                        <Col lg={4} md={6} className="d-flex align-items-end">
                            <Form.Check
                                type="switch"
                                id="hide-subscription-transactions"
                                label={t('cash_flow.hide_subscription_transactions')}
                                checked={hideSubscriptions}
                                onChange={(e) => {
                                    setHideSubscriptions(e.target.checked);
                                }}
                            />
                        </Col>
                    </Row>
                </FilterAccordion>
            </div>

            <div className="transaction-history__metrics mb-4">
                <button
                    type="button"
                    className={[
                        'transaction-history__metric-card',
                        'transaction-history__metric-card--warning',
                        cardFilter === null ? 'transaction-history__metric-card--active' : ''
                    ].join(' ')}
                    aria-pressed={cardFilter === null}
                    title={t('cash_flow.metric_card_filter_hint')}
                    onClick={() => handleCardClick(null)}
                >
                    <div className="transaction-history__metric-head">
                        <div>
                            <span className="transaction-history__metric-label">{t('cash_flow.total_transactions')}</span>
                            <strong className="transaction-history__metric-value text-warning">{stats.count}</strong>
                        </div>
                        <span className="transaction-history__metric-icon">
                            <i className="fas fa-exchange-alt"></i>
                        </span>
                    </div>
                </button>
                <button
                    type="button"
                    className={[
                        'transaction-history__metric-card',
                        'transaction-history__metric-card--success',
                        cardFilter === TransactionCashFlowType.IN ? 'transaction-history__metric-card--active' : ''
                    ].join(' ')}
                    aria-pressed={cardFilter === TransactionCashFlowType.IN}
                    title={t('cash_flow.metric_card_filter_hint')}
                    onClick={() => handleCardClick(TransactionCashFlowType.IN)}
                >
                    <div className="transaction-history__metric-head">
                        <div>
                            <span className="transaction-history__metric-label">{t('cash_flow.income')}</span>
                            <strong className="transaction-history__metric-value text-success">
                                {t('currency_symbol')} {convertDoubleToStringPTBR(stats.totalIncome)}
                            </strong>
                        </div>
                        <span className="transaction-history__metric-icon">
                            <i className="fas fa-arrow-up"></i>
                        </span>
                    </div>
                </button>
                <button
                    type="button"
                    className={[
                        'transaction-history__metric-card',
                        'transaction-history__metric-card--danger',
                        cardFilter === TransactionCashFlowType.OUT ? 'transaction-history__metric-card--active' : ''
                    ].join(' ')}
                    aria-pressed={cardFilter === TransactionCashFlowType.OUT}
                    title={t('cash_flow.metric_card_filter_hint')}
                    onClick={() => handleCardClick(TransactionCashFlowType.OUT)}
                >
                    <div className="transaction-history__metric-head">
                        <div>
                            <span className="transaction-history__metric-label">{t('cash_flow.expense')}</span>
                            <strong className="transaction-history__metric-value text-danger">
                                {t('currency_symbol')} {convertDoubleToStringPTBR(stats.totalExpense)}
                            </strong>
                        </div>
                        <span className="transaction-history__metric-icon">
                            <i className="fas fa-arrow-down"></i>
                        </span>
                    </div>
                </button>
                <div className="transaction-history__metric-card transaction-history__metric-card--primary">
                    <div className="transaction-history__metric-head">
                        <div>
                            <span className="transaction-history__metric-label">{t('balance')}</span>
                            <strong className={`transaction-history__metric-value ${stats.balance >= 0 ? 'text-primary' : 'text-danger'}`}>
                                {t('currency_symbol')} {convertDoubleToStringPTBR(stats.balance)}
                            </strong>
                        </div>
                        <span className="transaction-history__metric-icon">
                            <i className="fas fa-balance-scale"></i>
                        </span>
                    </div>
                </div>
            </div>

            <Row className="mb-3">
                <Col md={12}>
                    <ButtonGroup>
                        <Dropdown>
                            <Dropdown.Toggle className="me-2" variant="primary">
                                {t('actions')}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={() => redirectToNew()}>
                                    <SaveIcon /> {t('new')}
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </ButtonGroup>
                </Col>
            </Row>

            <GridCustomBase
                pageable={pageable}
                setPage={setPage}
                setSize={setSize}
                showLoading={showLoading}>

                <table className="table table-list i-table style-1 mb-0 border-0 transaction-history__table" id="guestTable-transactions" style={{ minWidth: 1120 }}>
                    <thead>
                        <tr>
                            <th>{t("cash_flow.cash")}</th>
                            <th>{t("branch")}</th>
                            <th>{t("date_time")}</th>
                            <th>{t("value")}</th>
                            <th>{t("payment_method_simplified")}</th>
                            <th>{t("description")}</th>
                            <th className="text-center">{t("type")}</th>
                            <th className="bg-none" style={{ width: 60 }} aria-label={t('actions')}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan="8" className="text-center py-5">
                                    <div className="text-muted">
                                        <i className="fas fa-exchange-alt fa-2x mb-3 d-block opacity-50"></i>
                                        <strong className="d-block mb-1">{t('cash_flow.transactions_empty_title')}</strong>
                                        <span>{t('cash_flow.transactions_empty_hint')}</span>
                                    </div>
                                </td>
                            </tr>
                        )}
                        {rows.map((row, index) => {
                            const isExpense = row.type === TransactionCashFlowType.OUT;
                            return (
                                <tr key={index} className="align-middle transaction-history__table-row">
                                    <td>
                                        <span className="fw-semibold">
                                            {row.cash?.openingUser?.fullName || '-'}
                                        </span>
                                    </td>
                                    <td>
                                        <Badge bg="info" className="badge-lg">
                                            {row.branch?.name || '-'}
                                        </Badge>
                                    </td>
                                    <td>
                                        <i className="fa fa-clock text-muted me-1"></i>
                                        {formatDateTime(row.transactionDate)}
                                    </td>
                                    <td>
                                        <strong className={isExpense ? "text-danger" : 'text-success'} style={{ fontSize: '1.1em' }}>
                                            <i className={`fas fa-${isExpense ? 'minus' : 'plus'}-circle me-1`}></i>
                                            {t('currency_symbol')} {convertDoubleToStringPTBR(row.value)}
                                        </strong>
                                    </td>
                                    <td>
                                        {row.paymentMethod ? (
                                            <Badge bg="secondary" className="badge-lg">
                                                <i className="fa fa-credit-card me-1"></i>
                                                {t(row.paymentMethod.toLowerCase())}
                                            </Badge>
                                        ) : (
                                            <span className="text-muted">-</span>
                                        )}
                                    </td>
                                    <td>
                                        {row.description}
                                        {row.origin && row.origin !== 'MANUAL' && (
                                            <Badge bg="light" text="dark" className="ms-2 transaction-history__origin-badge">
                                                {t(`cash_flow.origin_${row.origin.toLowerCase()}`)}
                                            </Badge>
                                        )}
                                    </td>
                                    <td className="text-center">
                                        <Badge bg={isExpense ? "danger" : 'success'} className="badge-lg">
                                            <i className={`fa fa-${isExpense ? 'arrow-down' : 'arrow-up'} me-1`}></i>
                                            {row.type === TransactionCashFlowType.IN ? t('cash_flow.income') : t('cash_flow.expense')}
                                        </Badge>
                                    </td>
                                    <td className="transaction-history__actions-cell">
                                        <Dropdown className="d-inline-flex">
                                            <Dropdown.Toggle
                                                variant="link"
                                                className="transaction-history__row-action i-false"
                                                aria-label={t('actions')}
                                            >
                                                <i className="fas fa-ellipsis-v" aria-hidden="true"></i>
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu>
                                                <Dropdown.Item onClick={() => openDetail(row)}>
                                                    <DetailIcon className="me-2" /> Detalhes
                                                </Dropdown.Item>
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </GridCustomBase>
        </>
    );
}

export default TransactionCashFlowHistory;
